import { format } from 'date-fns/format';
import { useState } from 'react';
import { toast } from 'sonner';
import queryString from 'query-string';

import { useApiQuery, useFetch } from '@/modules/common';
import { EventOrder } from "@/modules/eventsV2/types";
import { type EventFiltersType } from "@/modules/eventsV2/hooks";

const currentDate = format(new Date(), 'yyyy-MM-dd');

export function useEventOrdersHistory(
  eventId: string,
  eventName: string,
  selected: string[],
  filters: EventFiltersType,
) {
  const fetchClient = useFetch();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const generateReport = async () => {
    setIsGeneratingReport(true);

    try {
      const response = await fetchClient(
        `/events/${eventId}/orders/report-csv?${queryString.stringify({
          orderIds: selected,
          searchText: filters.search,
          statuses: filters.statuses,
        }, { arrayFormat: 'bracket' })}`,
        {
          method: 'GET',
          asFormData: true,
          responseType: 'blob',
        }
      );
      const blob = new Blob([response], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `${eventName}_report_${currentDate}.csv`;

      document.body.appendChild(link);

      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const { data: ordersData, isFetching: isFetchingOrders } = useApiQuery<{
    items: EventOrder[];
  }>(
    `/events/${eventId}/orders?${queryString.stringify({
      searchText: filters.search,
      statuses: filters.statuses,
    }, { arrayFormat: 'bracket' })}&pageSize=1000`,
    {
      queryKey: ['eventOrders', eventId, filters],
      initialData: { items: [] },
    }
  );

  return {
    isFetchingOrders,
    ordersData: ordersData?.items || [],
    isGeneratingReport,
    generateReport,
  } as const;
}
